import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shuffle, Users, Home, Globe, Calendar, Briefcase, ArrowLeft, Zap, Target, Lightbulb, ChevronRight, Check, MessageSquare, Coffee } from 'lucide-react';
// Sample profile data
const profiles = [{
  id: 1,
  name: 'Marco Bianchi',
  role: 'UX Designer',
  company: 'Design Innovation',
  location: 'Milan',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg',
  skills: ['UI/UX', 'Design Thinking', 'Prototyping'],
  interests: ['Service Design', 'Design Systems', 'User Research'],
  matchPercentage: 94,
  matchReason: 'Marco has complementary skills to your profile and is working on similar projects in the design field.',
  suggestedTopic: 'Design Systems for enterprise applications'
}, {
  id: 2,
  name: 'Laura Rossi',
  role: 'Product Manager',
  company: 'Tech Solutions',
  location: 'Rome',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg',
  skills: ['Product Strategy', 'Agile', 'Market Research'],
  interests: ['AI Products', 'Customer Experience', 'Product Analytics'],
  matchPercentage: 92,
  matchReason: 'Laura has experience in launching digital products and can offer valuable perspectives on your current initiatives.',
  suggestedTopic: 'Go-to-market strategies for innovative products'
}, {
  id: 3,
  name: 'Andrea Marino',
  role: 'Data Scientist',
  company: 'AI Solutions',
  location: 'Turin',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg',
  skills: ['Machine Learning', 'Python', 'Data Visualization'],
  interests: ['AI Ethics', 'Predictive Analytics', 'Computer Vision'],
  matchPercentage: 89,
  matchReason: 'Andrea is developing AI solutions that could perfectly integrate with your current projects.',
  suggestedTopic: 'Integrating AI into existing applications'
}, {
  id: 4,
  name: 'Giulia Verdi',
  role: 'Frontend Developer',
  company: 'Web Innovators',
  location: 'Florence',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg',
  skills: ['React', 'TypeScript', 'CSS Animation'],
  interests: ['Web Performance', 'Accessibility', 'Design Systems'],
  matchPercentage: 91,
  matchReason: 'Giulia has technical expertise that complements your skills and is working with modern frameworks.',
  suggestedTopic: 'Best practices for frontend performance and accessibility'
}, {
  id: 5,
  name: 'Roberto Neri',
  role: 'Marketing Specialist',
  company: 'Growth Hackers',
  location: 'Naples',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/37RNAboQN8qk3BnTK1YzhT/irene-strong-TMt3JGoVlng-unsplash.jpg',
  skills: ['Digital Marketing', 'SEO', 'Content Strategy'],
  interests: ['Growth Hacking', 'Customer Acquisition', 'Analytics'],
  matchPercentage: 88,
  matchReason: 'Roberto can help you improve the visibility of your projects with innovative marketing strategies.',
  suggestedTopic: 'Content marketing strategies for tech products'
}];
// Predefined networking goals
const networkingGoals = [{
  id: 'skills',
  icon: <Lightbulb className="w-5 h-5" data-id="element-2815" />,
  title: 'Skill Development',
  description: 'Meet someone with complementary skills'
}, {
  id: 'project',
  icon: <Target className="w-5 h-5" data-id="element-2816" />,
  title: 'Collaboration',
  description: 'Find partners for a project'
}, {
  id: 'career',
  icon: <Briefcase className="w-5 h-5" data-id="element-2817" />,
  title: 'Career',
  description: 'Expand your professional network'
}, {
  id: 'random',
  icon: <Shuffle className="w-5 h-5" data-id="element-2818" />,
  title: 'Completely Random',
  description: 'Discover unexpected connections'
}];
export function RandomNetworkPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('select'); // 'select', 'spinning', 'result'
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [matchedProfile, setMatchedProfile] = useState(null);
  const [spinningProfiles, setSpinningProfiles] = useState([]);
  const spinningInterval = useRef(null);
  // Function to start the randomization process
  const startRandomize = goal => {
    setSelectedGoal(goal);
    setStep('spinning');
    // Initialize the profile array for animation
    setSpinningProfiles([...profiles]);
    // Start the interval to rapidly change profiles
    let counter = 0;
    const maxSpins = 20; // Number of changes before stopping
    const initialInterval = 100; // Initial milliseconds between changes
    const updateSpinningProfiles = () => {
      counter++;
      // Gradually slow down the interval
      const currentInterval = initialInterval + Math.floor(counter * 10);
      // Change the order of profiles to simulate spinning
      setSpinningProfiles(prev => {
        const newArray = [...prev];
        // Move the last element to the beginning
        newArray.unshift(newArray.pop());
        return newArray;
      });
      // Stop spinning after a certain number of iterations
      if (counter >= maxSpins) {
        clearInterval(spinningInterval.current);
        // Choose a random profile as the result
        const randomIndex = Math.floor(Math.random() * profiles.length);
        setMatchedProfile(profiles[randomIndex]);
        // Move to the result step
        setTimeout(() => {
          setStep('result');
        }, 500);
      } else {
        // Update the interval for the next cycle
        clearInterval(spinningInterval.current);
        spinningInterval.current = setInterval(updateSpinningProfiles, currentInterval);
      }
    };
    // Start the first interval
    spinningInterval.current = setInterval(updateSpinningProfiles, initialInterval);
  };
  // Clear the interval when the component is unmounted
  useEffect(() => {
    return () => {
      if (spinningInterval.current) {
        clearInterval(spinningInterval.current);
      }
    };
  }, []);
  // Function to restart
  const restart = () => {
    setStep('select');
    setSelectedGoal(null);
    setMatchedProfile(null);
  };
  return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans" data-id="element-2819">
      {/* Main content */}
      <div className="flex-grow bg-white pt-6 pb-20 md:pb-10" data-id="element-2820">
        <div className="max-w-6xl mx-auto px-0" data-id="element-2821">
          {/* Breadcrumbs navigation */}
          <nav className="flex items-center text-sm mb-6" data-id="element-2822">
            <button onClick={() => navigate('/networking')} className="flex items-center text-gray-600 hover:text-black" data-id="element-2823">
              <ArrowLeft className="h-4 w-4 mr-1" data-id="element-2824" />
              <span data-id="element-2825">Networking</span>
            </button>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" data-id="element-2826" />
            <span className="text-black font-medium" data-id="element-2827">Random Network</span>
          </nav>
          {/* Header */}
          <div className="mb-8" data-id="element-2828">
            <h1 className="text-2xl md:text-3xl font-light font-sharp-grotesk" data-id="element-2829">
              Random Network
            </h1>
          </div>
          {/* Content based on current step */}
          {step === 'select' && <div className="mb-8" data-id="element-2830">
              <p className="text-gray-600 mb-6" data-id="element-2831">
                Select a goal to find the right person for you. Our algorithm
                will randomly select someone with high compatibility.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-2832">
                {networkingGoals.map(goal => <div key={goal.id} onClick={() => startRandomize(goal)} className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-2833">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-4" data-id="element-2834">
                      {goal.icon}
                    </div>
                    <div data-id="element-2835">
                      <h3 className="font-medium mb-1" data-id="element-2836">{goal.title}</h3>
                      <p className="text-sm text-gray-600" data-id="element-2837">
                        {goal.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}
          {step === 'spinning' && <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-id="element-2838">
              <div className="text-center mb-8" data-id="element-2839">
                <h2 className="text-xl font-medium mb-2" data-id="element-2840">
                  Finding the perfect match...
                </h2>
                <p className="text-gray-600" data-id="element-2841">
                  Our algorithm is looking for the ideal person for your goal:
                  <span className="font-medium" data-id="element-2842"> {selectedGoal?.title}</span>
                </p>
              </div>
              <div className="flex flex-col items-center" data-id="element-2843">
                {/* Slot machine effect */}
                <div className="relative w-32 h-32 mb-6 overflow-hidden" data-id="element-2844">
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white z-10 pointer-events-none" data-id="element-2845"></div>
                  <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white z-10 pointer-events-none" data-id="element-2846"></div>
                  {/* Container for spinning profiles */}
                  <div className="relative w-full h-full" data-id="element-2847">
                    {spinningProfiles.slice(0, 3).map((profile, index) => <div key={`${profile.id}-${index}`} className="absolute inset-0 transition-all duration-300" style={{
                  transform: `translateY(${(index - 1) * 100}%)`,
                  opacity: index === 1 ? 1 : 0.5
                }} data-id="element-2848">
                        <img src={profile.image} alt={profile.name} className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg" data-id="element-2849" />
                      </div>)}
                  </div>
                </div>
                <div className="animate-pulse" data-id="element-2850">
                  <Zap className="w-8 h-8 text-black" data-id="element-2851" />
                </div>
              </div>
            </div>}
          {step === 'result' && matchedProfile && <>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4" data-id="element-2852">
                {/* Header with match percentage */}
                <div className="bg-black text-white p-4 flex justify-between items-center" data-id="element-2853">
                  <h2 className="text-xl font-medium" data-id="element-2854">Your match</h2>
                  <div className="flex items-center bg-white text-black rounded-full px-3 py-1" data-id="element-2855">
                    <Zap className="w-4 h-4 mr-1 text-yellow-500" data-id="element-2856" />
                    <span className="font-medium" data-id="element-2857">
                      {matchedProfile.matchPercentage}% match
                    </span>
                  </div>
                </div>
                {/* Profile content */}
                <div className="p-6" data-id="element-2858">
                  <div className="flex flex-col md:flex-row gap-6" data-id="element-2859">
                    {/* Left column - profile info */}
                    <div className="md:w-1/3 flex flex-col items-center" data-id="element-2860">
                      <div className="w-32 h-32 rounded-full overflow-hidden mb-4 border-4 border-white shadow-lg" data-id="element-2861">
                        <img src={matchedProfile.image} alt={matchedProfile.name} className="w-full h-full object-cover" data-id="element-2862" />
                      </div>
                      <h3 className="text-xl font-medium text-center mb-1" data-id="element-2863">
                        {matchedProfile.name}
                      </h3>
                      <p className="text-gray-600 text-center mb-2" data-id="element-2864">
                        {matchedProfile.role} @ {matchedProfile.company}
                      </p>
                      <p className="text-gray-600 text-sm text-center mb-4" data-id="element-2865">
                        {matchedProfile.location}
                      </p>
                      {/* Skills and interests */}
                      <div className="w-full" data-id="element-2866">
                        <h4 className="text-sm font-medium mb-2" data-id="element-2867">Skills</h4>
                        <div className="flex flex-wrap gap-2 mb-4" data-id="element-2868">
                          {matchedProfile.skills.map((skill, index) => <span key={index} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2869">
                              {skill}
                            </span>)}
                        </div>
                        <h4 className="text-sm font-medium mb-2" data-id="element-2870">Interests</h4>
                        <div className="flex flex-wrap gap-2" data-id="element-2871">
                          {matchedProfile.interests.map((interest, index) => <span key={index} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2872">
                              {interest}
                            </span>)}
                        </div>
                      </div>
                    </div>
                    {/* Right column - match details */}
                    <div className="md:w-2/3 border-t md:border-t-0 md:border-l border-gray-200 md:pl-6 pt-6 md:pt-0" data-id="element-2873">
                      <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-2874">
                        <h3 className="font-medium mb-2" data-id="element-2875">Why this match?</h3>
                        <p className="text-gray-600" data-id="element-2876">
                          {matchedProfile.matchReason}
                        </p>
                      </div>
                      <div className="mb-6" data-id="element-2877">
                        <h3 className="font-medium mb-3" data-id="element-2878">
                          Suggested networking topic
                        </h3>
                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4" data-id="element-2879">
                          <div className="flex items-start" data-id="element-2880">
                            <Lightbulb className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2881" />
                            <p data-id="element-2882">{matchedProfile.suggestedTopic}</p>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-gray-200 pt-6" data-id="element-2883">
                        <h3 className="font-medium mb-4" data-id="element-2884">Next steps</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2885">
                          <button className="flex items-center justify-center bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2886">
                            <Coffee className="w-4 h-4 mr-2" data-id="element-2887" />
                            Suggest a Coffee Break
                          </button>
                          <button className="flex items-center justify-center border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2888">
                            <MessageSquare className="w-4 h-4 mr-2" data-id="element-2889" />
                            Send a message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* CTA moved to the bottom, outside the card */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col space-y-3 mb-8" data-id="element-2890">
                <button onClick={restart} className="flex items-center justify-center bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors w-fit" data-id="element-2891">
                  <Shuffle className="w-4 h-4 mr-2" data-id="element-2892" />
                  Find another match
                </button>
                <div className="flex items-center text-gray-600" data-id="element-2893">
                  <Check className="w-4 h-4 mr-2 text-green-500" data-id="element-2894" />
                  Suggested based on your goal: {selectedGoal?.title}
                </div>
              </div>
            </>}
        </div>
      </div>
      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 z-50" data-id="element-2895">
        <button onClick={() => navigate('/')} className="flex flex-col items-center" data-id="element-2896">
          <Home className="w-5 h-5 text-gray-400" data-id="element-2897" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2898">Home</span>
        </button>
        <button onClick={() => navigate('/connections')} className="flex flex-col items-center" data-id="element-2899">
          <Users className="w-5 h-5 text-gray-400" data-id="element-2900" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2901">Connections</span>
        </button>
        <button className="flex flex-col items-center" data-id="element-2902">
          <Globe className="w-5 h-5 text-black" data-id="element-2903" />
          <span className="text-xs mt-1 text-black" data-id="element-2904">Networking</span>
        </button>
        <button onClick={() => navigate('/collaboration')} className="flex flex-col items-center" data-id="element-2905">
          <Briefcase className="w-5 h-5 text-gray-400" data-id="element-2906" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2907">Collaboration</span>
        </button>
        <button onClick={() => navigate('/events')} className="flex flex-col items-center" data-id="element-2908">
          <Calendar className="w-5 h-5 text-gray-400" data-id="element-2909" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2910">Events</span>
        </button>
      </div>
    </div>;
}
export default {
  RandomNetworkPage
};